import CloseIcon from "@mui/icons-material/Close";
import {
  Drawer,
  IconButton,
  Box,
  Typography,
  Stack,
  Divider,
  Chip
} from "@mui/material";
import { formatDateTime } from "@qubit/autoparts";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import {
  selectPongs,
  selectAllPongHistories
} from "~/features/andon/andon.slice";

import { extractUserNameFromAuth0Id } from "~/lib/helpers";
import { WorkstationSummaryDto } from "~/types/api";

import {
  getMinMaxAvg,
  calculateMemoryUsagePercentage,
  getColorForValue,
  getMemoryColor,
  formatBytes,
  formatMsOrSec,
  formatMinMaxAvgT,
  hasValidHistory
} from "./workstationsMonitor.utils";

export interface WorkstationDetailDrawerProps {
  open: boolean;
  workstation: WorkstationSummaryDto | null;
  onClose: () => void;
}

export default function WorkstationDetailDrawer({
  open,
  workstation,
  onClose
}: WorkstationDetailDrawerProps) {
  const { t } = useTranslation();

  const pongs = useAppSelector(selectPongs);
  const pongHistories = useAppSelector(selectAllPongHistories);

  const pong = workstation ? pongs?.[workstation.id] : undefined;
  const pongData = pong?.pongData;
  const userName = workstation?.userId
    ? extractUserNameFromAuth0Id(workstation.userId)
    : "";

  const pongHistory = pongHistories?.[workstation?.id ?? ""] ?? [];

  const downlinkHistory = pongHistory.map(
    (h) => h.pongData.network?.downlink ?? 0
  );
  const rttHistory = pongHistory.map((h) => h.pongData.network?.rtt ?? 0);
  const fpsHistory = pongHistory.map((h) => h.pongData.fps ?? 0);
  const loopHistory = pongHistory.map((h) => h.loopTime ?? 0);
  const memoryHistory = pongHistory.map((h) => {
    const mem = h.pongData.memory;
    return mem?.usedJSHeapSize && mem?.jsHeapSizeLimit
      ? (mem.usedJSHeapSize / mem.jsHeapSizeLimit) * 100
      : 0;
  });

  const downlinkStats = getMinMaxAvg(downlinkHistory);
  const rttStats = getMinMaxAvg(rttHistory);
  const fpsStats = getMinMaxAvg(fpsHistory);
  const loopStats = getMinMaxAvg(loopHistory);
  const memStats = getMinMaxAvg(memoryHistory);

  const pingLoopSplit = formatMsOrSec(pong?.pongLoopTime ?? "");
  const minSplit = formatMsOrSec(loopStats.min);
  const maxSplit = formatMsOrSec(loopStats.max);
  const avgSplit = formatMsOrSec(loopStats.avg);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => {}}
      variant="persistent"
      ModalProps={{
        hideBackdrop: true,
        onBackdropClick: (e) => e.stopPropagation()
      }}
      PaperProps={{
        sx: {
          width: "40vw",
          p: 0,
          boxShadow: "-4px 4px 20px rgba(0, 0, 0, .25)"
        }
      }}
    >
      {/* Box within a box */}
      <Box display="flex" flexDirection="column" height="100%">
        {/* Close Button */}
        <Box display="flex" justifyContent="flex-start" p={1} mt={5}>
          <IconButton aria-label="Close details" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable Content */}
        <Box flex={1} overflow="auto" p={2}>
          {!workstation ? (
            <Typography color="text.disabled">{`${t("no data found")}`}</Typography>
          ) : (
            // HEADER
            <Stack>
              {/* workstation id */}
              <Typography variant="h6">{workstation.deviceId}</Typography>
              {/* subheader information */}
              <Stack mb={1}>
                <Typography>{`${t("id")}: ${workstation.id}`}</Typography>
                <Typography>{`${t("user")}: ${userName}`}</Typography>
                <Typography>
                  {`${t("mode")}: ${workstation.mode !== "Undefined" ? workstation.mode : "-"}`}
                </Typography>
                <Typography>
                  {`${t("status")}: `}
                  <span
                    style={{
                      color:
                        workstation.status === "Active"
                          ? "#2e7d32"
                          : workstation.status === "Faulted"
                            ? "#d32f2f"
                            : undefined
                    }}
                  >
                    {workstation.status}
                  </span>
                </Typography>
                {workstation.roles?.length > 0 && (
                  <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
                    {workstation.roles.map((role) => (
                      <Chip
                        key={role}
                        label={role}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                )}
                <Typography>
                  {`${t("pong time")}: `}
                  {pong?.timePingSent
                    ? formatDateTime(new Date(pong.timePingSent))
                    : ""}
                </Typography>
              </Stack>

              <Divider />

              {/* NETWORK */}
              <Stack mt={1}>
                <Typography variant="subtitle2">{t("network")}</Typography>
                <Stack sx={{ backgroundColor: "gray.light", p: 1 }}>
                  <Typography>
                    {t("network type")}:{" "}
                    {pongData?.network?.effectiveType ?? ""}
                  </Typography>

                  {/* Downlink */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between"
                    }}
                  >
                    <Typography>
                      {t("downlink")}:{" "}
                      <span
                        style={{
                          color: getColorForValue(
                            Number(pongData?.network?.downlink ?? 0),
                            "downlink"
                          )
                        }}
                      >
                        {pongData?.network?.downlink != null
                          ? `${pongData.network.downlink} Mbps`
                          : ""}
                      </span>
                    </Typography>

                    {hasValidHistory(downlinkHistory) && (
                      <Typography>
                        {formatMinMaxAvgT({
                          t,
                          min: downlinkStats.min,
                          max: downlinkStats.max,
                          avg: downlinkStats.avg
                        })}
                      </Typography>
                    )}
                  </Box>
                  {/* RTT */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between"
                    }}
                  >
                    <Typography>
                      {t("rtt")}:{" "}
                      <span
                        style={{
                          color: getColorForValue(
                            Number(pongData?.network?.rtt ?? 0),
                            "rtt"
                          )
                        }}
                      >
                        {pongData?.network?.rtt != null
                          ? `${pongData.network.rtt} ms`
                          : ""}
                      </span>
                    </Typography>
                    {hasValidHistory(rttHistory) && (
                      <Typography>
                        {formatMinMaxAvgT({
                          t,
                          min: rttStats.min,
                          max: rttStats.max,
                          avg: rttStats.avg
                        })}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Stack>

              {/* PERFORMANCE */}
              <Stack mt={1}>
                <Typography variant="subtitle2">{t("performance")}</Typography>
                <Stack sx={{ backgroundColor: "gray.light", p: 1 }}>
                  {/* FPS */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between"
                    }}
                  >
                    <Typography>
                      {t("fps")}:{" "}
                      <span
                        style={{
                          color: getColorForValue(
                            Number(pongData?.fps ?? 0),
                            "fps"
                          )
                        }}
                      >
                        {pongData?.fps ?? ""}
                      </span>
                    </Typography>
                    {hasValidHistory(fpsHistory) && (
                      <Typography>
                        {formatMinMaxAvgT({
                          t,
                          min: fpsStats.min,
                          max: fpsStats.max,
                          avg: fpsStats.avg
                        })}
                      </Typography>
                    )}
                  </Box>

                  {/* Ping Loop Time */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between"
                    }}
                  >
                    <Typography>
                      {t("ping loop time")}: {pingLoopSplit.value}
                      {pingLoopSplit.unit ? ` ${pingLoopSplit.unit}` : ""}
                    </Typography>
                    {hasValidHistory(loopHistory) && (
                      <Typography>
                        {formatMinMaxAvgT({
                          t,
                          min: minSplit.value,
                          minUnit: minSplit.unit,
                          max: maxSplit.value,
                          maxUnit: maxSplit.unit,
                          avg: avgSplit.value,
                          avgUnit: avgSplit.unit
                        })}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Stack>

              {/* MEMORY */}
              <Stack mt={1}>
                <Typography variant="subtitle2">{t("memory")}</Typography>
                <Stack sx={{ backgroundColor: "gray.light", p: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between"
                    }}
                  >
                    <Typography>
                      {t("percent used")}:{" "}
                      <span
                        style={{
                          color: getMemoryColor(pongData?.memory)
                        }}
                      >
                        {typeof pongData?.memory?.usedJSHeapSize === "number" &&
                        typeof pongData?.memory?.jsHeapSizeLimit === "number"
                          ? `${calculateMemoryUsagePercentage(pongData.memory)}%`
                          : ""}
                      </span>
                    </Typography>
                    {hasValidHistory(memoryHistory) && (
                      <Typography>
                        {formatMinMaxAvgT({
                          t,
                          min: memStats.min,
                          minUnit: "%",
                          max: memStats.max,
                          maxUnit: "%",
                          avg: memStats.avg,
                          avgUnit: "%"
                        })}
                      </Typography>
                    )}
                  </Box>
                  <Typography>
                    {t("memory used")}:{" "}
                    {formatBytes(pongData?.memory?.usedJSHeapSize) ?? ""}
                  </Typography>
                  <Typography>
                    {t("memory total")}:{" "}
                    {formatBytes(pongData?.memory?.totalJSHeapSize) ?? ""}
                  </Typography>
                  <Typography>
                    {t("memory limit")}:{" "}
                    {formatBytes(pongData?.memory?.jsHeapSizeLimit) ?? ""}
                  </Typography>
                </Stack>
              </Stack>

              {/* SYSTEM */}
              <Stack mt={1}>
                <Typography variant="subtitle2">{t("system")}</Typography>
                <Stack sx={{ backgroundColor: "gray.light", p: 1 }}>
                  <Typography>
                    {t("cpu cores")}: {pongData?.cpu?.cores ?? ""}
                  </Typography>
                  <Typography>
                    {t("platform")}:{" "}
                    {pongData?.userAgentLowEntropy?.platform ?? ""}
                  </Typography>
                  <Typography>
                    {t("platform version")}:{" "}
                    {pongData?.userAgentHighEntropy?.platformVersion ?? ""}
                  </Typography>
                  <Typography>
                    {t("browser")}: {pongData?.userAgentParsed?.browser ?? ""}
                  </Typography>
                  <Typography>
                    {t("ua full version")}:{" "}
                    {pongData?.userAgentHighEntropy?.uaFullVersion ?? ""}
                  </Typography>
                  <Typography>
                    {t("device model")}:{" "}
                    {pongData?.userAgentHighEntropy?.model ?? ""}
                  </Typography>
                  <Typography>
                    {t("mobile")}:{" "}
                    {pongData?.userAgentLowEntropy?.mobile ? t("yes") : t("no")}
                  </Typography>
                  <Typography>
                    {t("architecture")}:{" "}
                    {pongData?.userAgentHighEntropy?.architecture ?? ""}
                  </Typography>
                  <Typography>
                    {t("bitness")}:{" "}
                    {pongData?.userAgentHighEntropy?.bitness ?? ""}
                  </Typography>
                  <Typography>
                    {t("time zone")}: {pongData?.timeZone ?? ""}
                  </Typography>
                  <Typography>
                    {t("timezone offset")}:{" "}
                    {typeof pongData?.timeZoneOffsetMinutes === "number"
                      ? pongData.timeZoneOffsetMinutes
                      : ""}
                  </Typography>
                </Stack>
              </Stack>

              {/* SCREEN */}
              <Stack mt={1}>
                <Typography variant="subtitle2">{t("screen")}</Typography>
                <Stack sx={{ backgroundColor: "gray.light", p: 1 }}>
                  <Typography>
                    {t("screen size")}:{" "}
                    {pongData?.screen?.width && pongData?.screen?.height
                      ? `${pongData.screen.width} x ${pongData.screen.height}`
                      : ""}
                  </Typography>
                  <Typography>
                    {t("available size")}:{" "}
                    {pongData?.screen?.availWidth &&
                    pongData?.screen?.availHeight
                      ? `${pongData.screen.availWidth} x ${pongData.screen.availHeight}`
                      : ""}
                  </Typography>
                  <Typography>
                    {t("device pixel ratio")}:{" "}
                    {typeof pongData?.screen?.pixelRatio === "number"
                      ? pongData.screen.pixelRatio.toFixed(3)
                      : ""}
                  </Typography>
                  <Typography>
                    {t("window size")}:{" "}
                    {pongData?.screen?.innerWidth &&
                    pongData?.screen?.innerHeight
                      ? `${pongData.screen.innerWidth} x ${pongData.screen.innerHeight}`
                      : ""}
                  </Typography>
                </Stack>
              </Stack>

              {/* NAVIGATION METRICS */}
              {pongData?.navigation && (
                <Stack mt={1}>
                  <Typography variant="subtitle2">{t("navigation")}</Typography>
                  <Stack sx={{ backgroundColor: "gray.light", p: 1 }}>
                    <Typography>
                      {t("type")}: {pongData.navigation.type}
                    </Typography>
                    <Typography>
                      {t("transfer size")}:{" "}
                      {pongData.navigation.transferSize === 0
                        ? "0 B (cached)"
                        : `${pongData.navigation.transferSize.toLocaleString()} B (network)`}
                    </Typography>
                    <Typography>
                      {t("duration")}: {pongData.navigation.duration} ms
                    </Typography>
                  </Stack>
                </Stack>
              )}

              {/* VERSION */}
              {(pongData?.clientVersion || pongData?.serverVersion) && (
                <Stack mt={1}>
                  <Typography variant="subtitle2">{t("version")}</Typography>
                  <Stack sx={{ backgroundColor: "gray.light", p: 1 }}>
                    <Typography>
                      {t("client")}: {pongData?.clientVersion ?? "-"}
                    </Typography>
                    <Typography>
                      {t("server")}:{" "}
                      {pongData?.serverVersion?.tag ||
                        pongData?.serverVersion?.hash ||
                        "-"}
                    </Typography>
                  </Stack>
                </Stack>
              )}

              {/* USER AGENT */}
              <Stack mt={1}>
                <Typography variant="subtitle2">{t("user agent")}</Typography>
                <Stack sx={{ backgroundColor: "gray.light", p: 1 }}>
                  <Typography
                    sx={{ whiteSpace: "pre-wrap", fontSize: "0.9em" }}
                  >
                    {pongData?.userAgent ?? ""}
                  </Typography>
                </Stack>
              </Stack>

              {/* PONG HISTORY (Ping Diagnostics) */}
              {pongHistory.length > 0 && (
                <Stack mt={1}>
                  <Typography variant="subtitle2">
                    {t("pong history")} ({pongHistory.length})
                  </Typography>
                  <Stack
                    sx={{
                      backgroundColor: "gray.light",
                      p: 1,
                      maxHeight: 200,
                      overflow: "auto"
                    }}
                  >
                    {pongHistory
                      .slice(-20)
                      .reverse()
                      .map((entry, idx) => {
                        const ts = new Date(entry.timestamp);
                        const rttVal = entry.pongData?.network?.rtt;
                        const loop = entry.loopTime;
                        return (
                          <Typography
                            key={idx}
                            sx={{ fontSize: "0.8rem", fontFamily: "monospace" }}
                          >
                            {formatDateTime(ts)}
                            {" — "}
                            {rttVal != null ? `RTT: ${rttVal}ms` : "RTT: -"}
                            {" | "}
                            {loop != null ? `Loop: ${loop}ms` : "Loop: -"}
                          </Typography>
                        );
                      })}
                  </Stack>
                </Stack>
              )}
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
